import { environment } from '../../../environments/environment';

/**
 * Live preview mode — the storefront embedded in the admin's Storefront
 * Settings, rendering the operator's DRAFT configuration instead of the
 * published one.
 *
 * Two stages:
 *  1. REQUESTED (decided here, synchronously, before Angular boots — the i18n
 *     service, routes and session need it early): the URL asks for it
 *     (`?preview=1&parent=<admin origin>[&lang=en|ar]`), the page is actually
 *     framed (window.parent !== window) and `parent` is a valid origin. The page
 *     then renders read-only (Home/Menu/guest screens, no cart or sign-in) —
 *     harmless even when forged, it only switches features off.
 *  2. VERIFIED (PreviewService, once GET /api/v1/brand has answered): `parent`
 *     must exactly equal the brand's `adminOrigin`; when the brand config has no
 *     `adminOrigin`, it must match environment.previewParentOrigins instead.
 *     Only a verified parent is told `ready` and has its drafts applied; a
 *     rejected one is told `rejected` (see PreviewService).
 *
 * Messaging (see PreviewService): the storefront only accepts messages whose
 * `event.source` is its parent window AND whose `event.origin` is exactly the
 * verified origin, and only ever posts to that exact origin — never `*`.
 * A hostile page framing the storefront with a forged `parent` gets nothing:
 * the browser drops a postMessage whose targetOrigin doesn't match the real
 * parent, and the real parent's messages fail the origin check.
 */
export const PREVIEW_PROTOCOL = 'sonic-storefront-preview';

export interface PreviewMode {
  /** Preview was requested (stage 1) — the page renders read-only. */
  active: boolean;
  /** The admin origin named in the URL — NOT trusted until PreviewService verifies it (null when not active). */
  parentOrigin: string | null;
  /** Language the admin asked the preview to render in (not persisted). */
  lang: 'en' | 'ar' | null;
}

const OFF: PreviewMode = { active: false, parentOrigin: null, lang: null };

/** Stage 1: does this URL, in a frame, ask for the live preview? Pure — `search` is `location.search`. */
export function readPreviewRequest(search: string, framed: boolean): PreviewMode {
  if (!framed) return OFF;
  const qs = new URLSearchParams(search);
  if (qs.get('preview') !== '1') return OFF;
  const origin = toOrigin(qs.get('parent'));
  if (!origin) return OFF;
  const lang = qs.get('lang');
  return { active: true, parentOrigin: origin, lang: lang === 'ar' ? 'ar' : lang === 'en' ? 'en' : null };
}

/**
 * Stage 2: may `parentOrigin` drive the preview? Exactly the brand's `adminOrigin` when the brand
 * config has one; otherwise one of the fallback patterns (environment.previewParentOrigins).
 */
export function isTrustedParent(
  parentOrigin: string | null | undefined,
  adminOrigin: string | null | undefined,
  fallback: RegExp[] = environment.previewParentOrigins,
): boolean {
  const parent = toOrigin(parentOrigin);
  if (!parent) return false;
  const admin = toOrigin(adminOrigin);
  if (admin) return parent === admin;
  return fallback.some((re) => re.test(parent));
}

/** Normalised origin (`scheme://host[:port]`, lowercase, no path) — null when not a valid http(s) URL. */
function toOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.origin : null;
  } catch {
    return null;
  }
}

function resolve(): PreviewMode {
  try {
    if (typeof window === 'undefined') return OFF;
    return readPreviewRequest(window.location.search, window.parent !== window);
  } catch {
    return OFF;
  }
}

/** Resolved once, before Angular boots (the i18n service needs the language early). */
export const PREVIEW: PreviewMode = resolve();
